import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Linkedin, Loader2, Power, Check, ArrowRight, RefreshCw, AlertCircle, ShieldAlert, HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { startLinkedInOAuth } from "@/lib/linkedinOAuth";

/**
 * Customer-facing connection dialog.
 *
 * No developer info: no App IDs, no env vars, no redirect URIs, no demo buttons.
 * Only handles:
 *   - "Continue with Facebook" (OAuth start)
 *   - Page selection (when multiple Pages are detected)
 *   - Disconnect
 *   - Friendly "Temporarily unavailable" copy when the platform isn't ready.
 */

function XGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.673l7.73-8.835L1.247 2.25h6.835l4.713 6.231 5.449-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

const PLATFORM_CONFIG = {
  facebook: {
    label: "Facebook",
    color: "#1877F2",
    Icon: Facebook,
    statusEndpoint: "/connections/facebook/setup-status",
    oauthStart: "/connections/facebook/oauth/start",
    disconnect: "/connections/facebook/disconnect",
    selectPage: "/connections/facebook/select-page",
    description: "We'll publish posts to the Facebook Page you choose.",
    primaryCtaIcon: Facebook,
    primaryCtaLabel: "Continue with Facebook",
  },
  instagram: {
    label: "Instagram",
    color: "#E4405F",
    Icon: Instagram,
    statusEndpoint: "/connections/instagram/setup-status-v2",
    connectFromFacebook: "/connections/instagram/connect-from-facebook",
    disconnect: "/connections/instagram/disconnect-v2",
    description: "Connect your Facebook Page first — we'll auto-detect your linked Instagram Business profile.",
    primaryCtaIcon: Instagram,
    primaryCtaLabel: "Use my linked Instagram",
  },
  x: {
    label: "X / Twitter",
    color: "#0f1419",
    Icon: XGlyph,
    statusEndpoint: "/connections/x/setup-status",
    oauthStart: "/connections/x/oauth/start",
    disconnect: "/connections/x/disconnect",
    description: "We'll publish text + single-image posts to your X account.",
    primaryCtaIcon: XGlyph,
    primaryCtaLabel: "Continue with X",
  },
  linkedin: {
    label: "LinkedIn",
    color: "#0A66C2",
    Icon: Linkedin,
    statusEndpoint: "/connections/linkedin/setup-status",
    oauthStart: "/connections/linkedin/oauth/start",
    disconnect: "/connections/linkedin/disconnect",
    description: "We'll publish text posts to your personal LinkedIn profile. Image posts and Company Pages are coming later.",
    primaryCtaIcon: Linkedin,
    primaryCtaLabel: "Continue with LinkedIn",
  },
};

export default function CustomerConnectionDialog({
  open, onOpenChange, platform, onChanged,
  onPlatformChangeConfirm, onPlatformChangeBlocked,
}) {
  const cfg = PLATFORM_CONFIG[platform];
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [fbHelpOpen, setFbHelpOpen] = useState(false);

  const refresh = async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const res = await api.get(cfg.statusEndpoint);
      setStatus(res.data);
    } catch (_e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && cfg) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, platform]);

  if (!cfg) return null;

  // Computed flags adapted per-platform
  const connectionDoc = status?.connection || null;
  const isFacebook = platform === "facebook";
  const isInstagram = platform === "instagram";
  const isX = platform === "x";
  const isLinkedIn = platform === "linkedin";

  const available =
    isFacebook
      ? !!status?.available
      : isInstagram
      ? !!status?.facebook_connected
      : isX
      ? !!status?.available
      : isLinkedIn
      ? !!status?.available
      : false;

  const connected =
    isFacebook
      ? !!status?.connected
      : !!connectionDoc?.connected;

  // Facebook two-stage flow: a basic-mode connection means the customer has
  // linked their Facebook account but hasn't granted Page publishing scopes
  // (which require Meta App Review). We surface a friendly upgrade CTA only
  // when the admin has confirmed Meta App Review is complete — otherwise we
  // show a clean "Requires Meta App Review" badge.
  const fbBasicOnly =
    isFacebook && connected && !status?.publishing_ready && status?.stage !== "demo";
  const fbPagePublishingEnabled = isFacebook ? !!status?.page_publishing_enabled : true;
  const fbUserName = isFacebook ? status?.fb_user_name : "";

  const selectedName =
    isFacebook
      ? status?.selected_page?.page_name
      : isInstagram
      ? connectionDoc?.selected?.name
      : isX
      ? connectionDoc?.selected?.name
      : null;

  const pages = (isFacebook ? status?.pages : []) || [];
  const linkedIg = (isInstagram ? status?.linked_ig_accounts : []) || [];
  // Instagram live posting is gated behind Meta App Review. Until the admin
  // flips the META_INSTAGRAM_LIVE_ENABLED flag we present an honest
  // "coming soon" state to customers (and demo + admin-only debug to admins).
  const igAwaitingReview = isInstagram && !!status?.awaiting_app_review;
  const Cta = cfg.primaryCtaIcon || ArrowRight;

  const startOAuth = async (stage, opts = {}) => {
    setBusy(true);
    try {
      // LinkedIn goes through the shared startLinkedInOAuth helper so
      // the card's expired-state Reconnect button and this dialog's
      // primary CTA cannot drift. All other platforms keep the
      // existing per-platform request shape (Facebook needs ?stage=).
      if (isLinkedIn) {
        const result = await startLinkedInOAuth({
          confirmConsume: !!opts.confirmConsume,
        });
        if (result && result.ok) return; // browser navigating to LinkedIn
        if (
          result &&
          result.code === "platform_change_confirm" &&
          onPlatformChangeConfirm
        ) {
          onOpenChange(false);
          onPlatformChangeConfirm({
            detail: result.detail,
            platform,
            retry: () => startOAuth(stage, { confirmConsume: true }),
          });
          return;
        }
        if (
          result &&
          result.code === "platform_change_limit" &&
          onPlatformChangeBlocked
        ) {
          onOpenChange(false);
          onPlatformChangeBlocked({ detail: result.detail, platform });
          return;
        }
        toast.error(
          (result && result.human) ||
            "We couldn't start the connection. Please try again."
        );
        return;
      }
      // Facebook supports a ?stage=basic|pages query; other platforms ignore it.
      const params = [];
      if (isFacebook && stage) params.push(`stage=${stage}`);
      if (opts.confirmConsume) params.push("confirm_consume=true");
      const url = cfg.oauthStart + (params.length ? `?${params.join("&")}` : "");
      const res = await api.get(url);
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
        return;
      }
      toast.error("We couldn't start the connection. Please try again.");
    } catch (e) {
      // Phase 17d.1 — 409 handshake for once-per-cycle platform-change
      // gate. The backend blocks OAuth start when this connect would be
      // a swap of the committed baseline; we surface the same confirm /
      // blocked dialogs the ManagePlatformsDialog uses.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const blockedBy = detail && typeof detail === "object" ? detail.blocked_by : null;
      if (status === 409 && blockedBy === "platform_change_confirm" && onPlatformChangeConfirm) {
        // Close ourselves so the parent-owned confirm dialog can render
        // cleanly, then hand off. The parent will call back into our
        // startOAuth via a retry ref if the user confirms.
        onOpenChange(false);
        onPlatformChangeConfirm({
          detail,
          platform,
          retry: () => startOAuth(stage, { confirmConsume: true }),
        });
        return;
      }
      if (status === 409 && blockedBy === "platform_change_limit" && onPlatformChangeBlocked) {
        onOpenChange(false);
        onPlatformChangeBlocked({ detail, platform });
        return;
      }
      // Surface the backend's reason when present (e.g. "LinkedIn is not
      // configured", "requires Meta approval"). Falls back to a generic
      // message for network failures.
      toast.error(
        (typeof detail === "string" && detail) ||
          (detail && typeof detail === "object" && detail.message) ||
          "We couldn't start the connection. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const selectFacebookPage = async (pageId) => {
    setBusy(true);
    try {
      await api.post(cfg.selectPage, { page_id: pageId });
      toast.success("Page selected");
      onChanged?.();
      await refresh();
    } catch (_e) {
      toast.error("Could not save your page selection");
    } finally {
      setBusy(false);
    }
  };

  const connectInstagramFromFacebook = async () => {
    setBusy(true);
    try {
      await api.post(cfg.connectFromFacebook);
      toast.success("Instagram connected");
      onChanged?.();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link Instagram");
    } finally {
      setBusy(false);
    }
  };

  const refreshInstagramDetection = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const res = await api.post("/connections/facebook/refresh-pages");
      setRefreshResult(res.data);
      await refresh();
      if (res.data?.ok) {
        toast.success(res.data.message || "Instagram detected");
      } else {
        toast.info(res.data?.message || "Refreshed Instagram detection");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not refresh Instagram detection";
      setRefreshResult({ ok: false, status: "error", message: detail });
      toast.error(detail);
    } finally {
      setRefreshing(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(cfg.disconnect);
      toast.success(`${cfg.label} disconnected`);
      onChanged?.();
      await refresh();
    } catch (_e) {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  const Icon = cfg.Icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-md"
        data-testid={`customer-connect-dialog-${platform}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: cfg.color }} /> Connect {cfg.label}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[color:var(--text-2)] leading-relaxed">{cfg.description}</p>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-[color:var(--text-3)]">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : !available ? (
          // ---------- TEMPORARILY UNAVAILABLE ----------
          <div className="mt-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4">
            <div className="font-display text-sm text-[color:var(--text-1)]">Temporarily unavailable</div>
            <div className="text-xs text-[color:var(--text-2)] mt-1.5 leading-relaxed">
              {cfg.label} connection is temporarily unavailable. Please check back soon.
            </div>
          </div>
        ) : connected ? (
          // ---------- CONNECTED ----------
          <div className="space-y-3 mt-2">
            <div
              className={
                "rounded-md border p-3 text-xs flex items-start gap-2 " +
                (fbBasicOnly
                  ? "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--accent-warm)]"
                  : "border-[color:rgba(46,204,113,0.35)] bg-[color:rgba(46,204,113,0.08)] text-[color:var(--success)]")
              }
            >
              <Check className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                {fbBasicOnly ? (
                  <>
                    <div className="text-[color:var(--text-1)] font-medium">
                      {fbUserName ? `${fbUserName} is connected` : "Your Facebook account is connected"}
                    </div>
                    <div className="text-[color:var(--text-2)] mt-1 leading-relaxed">
                      {fbPagePublishingEnabled
                        ? "Page publishing isn't enabled yet. Click below to grant the additional permissions we need to post to your Facebook Page."
                        : "Page publishing will be available once publishing permissions are enabled."}
                    </div>
                  </>
                ) : selectedName ? (
                  <span>
                    Connected — publishing to <strong>{selectedName}</strong>.
                  </span>
                ) : (
                  <span>Connected to {cfg.label}.</span>
                )}
              </div>
            </div>

            {fbBasicOnly && fbPagePublishingEnabled ? (
              <Button
                onClick={() => startOAuth("pages")}
                disabled={busy}
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="customer-fb-enable-page-publishing"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Facebook className="h-4 w-4 mr-2" />}
                Enable Page publishing
              </Button>
            ) : fbBasicOnly ? (
              <div
                className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 text-xs text-[color:var(--text-2)] flex items-center gap-2"
                data-testid="customer-fb-publishing-locked"
              >
                <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(255,138,61,0.15)] text-[color:var(--accent-warm)] border border-[color:rgba(255,138,61,0.3)]">
                  Page publishing
                </span>
                <span>Requires Meta App Review · coming soon</span>
              </div>
            ) : null}

            {isFacebook && !fbBasicOnly && pages.length > 1 ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mb-2">
                  Switch page
                </div>
                <div className="space-y-1.5">
                  {pages.map((p) => (
                    <button
                      key={p.page_id}
                      type="button"
                      onClick={() => selectFacebookPage(p.page_id)}
                      disabled={busy}
                      data-testid={`customer-fb-page-${p.page_id}`}
                      className={
                        "w-full text-left rounded-md border px-3 py-2 text-xs flex items-center justify-between transition " +
                        (status?.selected_page?.page_id === p.page_id
                          ? "border-[color:var(--brand-primary)] bg-[color:rgba(0,182,165,0.08)] text-[color:var(--text-1)]"
                          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]")
                      }
                    >
                      <span>{p.page_name}</span>
                      {status?.selected_page?.page_id === p.page_id ? <Check className="h-3 w-3" /> : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex items-center justify-end pt-2 border-t border-[color:var(--border-subtle)]">
              <Button
                variant="ghost"
                onClick={disconnect}
                disabled={busy}
                className="text-[color:var(--danger)] hover:text-[color:var(--danger)]"
                data-testid={`customer-disconnect-${platform}`}
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Power className="h-4 w-4 mr-1" />}
                Disconnect
              </Button>
            </div>
          </div>
        ) : isInstagram ? (
          // ---------- INSTAGRAM PRE-CONNECT ----------
          <div className="space-y-3 mt-2">
            {/* Awaiting-review banner. Customers see this alone (nothing else).
                Admins see it as a heads-up and STILL get the detection UI +
                "Use this" button below so we can validate the Meta reviewer
                demo flow before public Meta approval. */}
            {igAwaitingReview ? (
              <div
                className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.05)] p-3"
                data-testid="customer-ig-awaiting-review"
              >
                <div className="flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--accent-warm)]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-semibold text-[color:var(--text-1)]">
                      {isAdmin ? "Admin preview — Meta approval pending" : "Coming soon — Meta approval pending"}
                    </div>
                    <div className="text-xs text-[color:var(--text-2)] mt-1 leading-relaxed">
                      {isAdmin
                        ? "Instagram publishing is gated for customers until Meta App Review is approved. As an admin you can still detect, connect, and test the Meta reviewer demo flow below."
                        : "Instagram publishing is coming soon. This platform requires Meta approval before live posting can be enabled."}
                    </div>
                    {!isAdmin ? (
                      <div className="text-[11px] text-[color:var(--text-3)] mt-2 leading-relaxed">
                        {"You'll need an Instagram Business or Creator account linked to a Facebook Page. We'll auto-detect it once Meta approves the permissions for Postmoro."}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Detection UI. Rendered for: non-admin customers (only when
                NOT awaiting review, as before) AND admins ALWAYS. */}
            {(!igAwaitingReview || isAdmin) ? (
              linkedIg.length === 0 ? (
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--text-2)] leading-relaxed" data-testid="customer-ig-no-link-message">
                  {status?.detection_message
                    || "We didn't find an Instagram Business or Creator account linked to your Facebook Page yet. In the Instagram app, switch your account to "}
                  {!status?.detection_message ? (
                    <>
                      <strong>Professional → Business</strong>{", then link it to your Facebook Page in Instagram settings — we'll pick it up automatically."}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
                    Found via your Facebook Page
                  </div>
                  {linkedIg.map((l) => (
                    <div
                      key={l.ig_business_account_id}
                      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs flex items-center justify-between"
                    >
                      <div className="min-w-0">
                        <div className="text-[color:var(--text-1)] font-medium truncate">{l.page_name}</div>
                        <div className="text-[color:var(--text-3)]">Linked Instagram Business account</div>
                        <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 font-mono tabular-nums truncate" title={l.ig_business_account_id}>
                          IG ID: {l.ig_business_account_id}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={connectInstagramFromFacebook}
                        disabled={busy}
                        className="bg-[#E4405F] hover:bg-[#cc3957] text-white text-xs shrink-0"
                        data-testid="customer-ig-use-link"
                      >
                        Use this
                      </Button>
                    </div>
                  ))}
                </div>
              )
            ) : null}

            {/* Refresh detection — visible for admins even during awaiting-review
                so we can re-probe /me/accounts after wiring the IG business
                account to the Facebook Page. Customers still get the refresh
                button only when live IG is enabled. */}
            {(!igAwaitingReview || isAdmin) ? (
              <div className="flex items-center justify-between gap-2 pt-1">
                <div className="text-[11px] text-[color:var(--text-3)] leading-relaxed">
                  {"Just linked Instagram to your Page? Click refresh — we'll re-ask Facebook."}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshInstagramDetection}
                  disabled={refreshing}
                  className="shrink-0 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]"
                  data-testid="customer-ig-refresh-detection"
                >
                  {refreshing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Refresh Instagram detection
                </Button>
              </div>
            ) : null}

            {/* Customer-friendly refresh outcome line */}
            {refreshResult && !refreshResult.ok ? (
              <div
                className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.05)] p-2.5 text-[11px] text-[color:var(--accent-warm)] flex items-start gap-2"
                data-testid="customer-ig-refresh-warning"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="leading-relaxed text-[color:var(--text-2)]">{refreshResult.message}</span>
              </div>
            ) : null}

            {/* Admin debug surface — only visible when admin mode is unlocked */}
            {isAdmin && (status?.admin_debug || refreshResult?.admin_debug || igAwaitingReview) ? (
              <details
                className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.03)] p-2.5 text-[11px] text-[color:var(--text-2)]"
                data-testid="admin-ig-debug-panel"
                open={!linkedIg.length || igAwaitingReview}
              >
                <summary className="cursor-pointer text-[color:var(--accent-warm)] flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Admin debug: Instagram detection
                </summary>
                {(() => {
                  const dbg = refreshResult?.admin_debug || status?.admin_debug || {};
                  const pages = dbg.pages || [];
                  const missingDet = dbg.missing_for_detection || [];
                  const missingPub = dbg.missing_for_publishing || [];
                  const reqList = dbg.app_review_requirements || status?.connection?.app_review_requirements || [];
                  return (
                    <div className="mt-2 space-y-1.5 font-mono text-[10px] leading-relaxed">
                      <div>
                        instagram_live_enabled:{" "}
                        <span className={dbg.instagram_live_enabled ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
                          {String(!!dbg.instagram_live_enabled)}
                        </span>
                        {" · "}
                        fb_page_publishing_enabled:{" "}
                        <span className={dbg.fb_page_publishing_enabled ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
                          {String(!!dbg.fb_page_publishing_enabled)}
                        </span>
                      </div>
                      <div>fb_user_id: <span className="text-[color:var(--text-1)]">{dbg.fb_user_id || "—"}</span> ({dbg.fb_user_name || "—"})</div>
                      <div>fb_mode: <span className="text-[color:var(--text-1)]">{dbg.fb_mode || "real"}</span></div>
                      {dbg.fb_intended_stage || dbg.intended_stage_at_connect ? (
                        <div>
                          intended_stage: <span className="text-[color:var(--text-1)]">{dbg.fb_intended_stage || dbg.intended_stage_at_connect}</span>
                        </div>
                      ) : null}
                      {(dbg.fb_requested_scopes?.length || dbg.requested_scopes_at_connect?.length) ? (
                        <div>requested: <span className="text-[color:var(--text-1)] break-all">{(dbg.fb_requested_scopes || dbg.requested_scopes_at_connect).join(" ")}</span></div>
                      ) : null}
                      <div>granted: <span className="text-[color:var(--text-1)] break-all">{(dbg.fb_granted_scopes || dbg.granted_scopes || []).join(" ") || "—"}</span></div>
                      {(dbg.fb_declined_scopes?.length || dbg.declined_scopes?.length) ? (
                        <div>declined: <span className="text-[color:var(--danger)] break-all">{(dbg.fb_declined_scopes || dbg.declined_scopes).join(" ")}</span></div>
                      ) : null}
                      <div>
                        has_pages_show_list: <span className={dbg.has_pages_listing ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
                          {String(!!dbg.has_pages_listing)}
                        </span>
                        {" · "}
                        has_instagram_basic: <span className={dbg.has_instagram_basic ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
                          {String(!!dbg.has_instagram_basic)}
                        </span>
                      </div>
                      {missingDet.length ? (
                        <div className="text-[color:var(--danger)]">
                          Missing for detection: {missingDet.join(", ")} — disconnect FB and reconnect approving these.
                        </div>
                      ) : null}
                      {missingPub.length ? (
                        <div className="text-[color:var(--accent-warm)]">
                          Missing for publishing: {missingPub.join(", ")}
                        </div>
                      ) : null}
                      <div>pages_total: <span className="text-[color:var(--text-1)]">{dbg.pages_total ?? pages.length}</span> · pages_with_instagram: <span className="text-[color:var(--text-1)]">{dbg.pages_with_instagram ?? pages.filter((p) => p.instagram_business_account_id).length}</span></div>
                      {dbg.pages_fetch_error ? (
                        <div className="text-[color:var(--danger)] break-all">graph_error: {dbg.pages_fetch_error}</div>
                      ) : null}
                      {dbg.pages_fetch_debug && Object.keys(dbg.pages_fetch_debug).length ? (
                        <details className="mt-1 rounded border border-[color:var(--border-subtle)] p-1.5 bg-[color:var(--surface-3)]" open>
                          <summary className="cursor-pointer text-[color:var(--text-3)] text-[10px] uppercase tracking-wider">
                            Graph API discovery endpoints
                          </summary>
                          <div className="mt-1 space-y-0.5">
                            <div>
                              token_tail: <span className="text-[color:var(--text-1)]">…{dbg.pages_fetch_debug.token_tail || "—"}</span>
                              {" · "}
                              requested_at: <span className="text-[color:var(--text-1)]">{dbg.pages_fetch_debug.requested_at || "—"}</span>
                            </div>
                            <div>total_pages_found: <span className={dbg.pages_fetch_debug.raw_data_count > 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>{dbg.pages_fetch_debug.raw_data_count ?? 0}</span></div>
                            {dbg.pages_fetch_debug.endpoints_tried?.length ? (
                              <div className="mt-1 space-y-0.5">
                                <div className="text-[color:var(--text-3)]">endpoints_tried:</div>
                                {dbg.pages_fetch_debug.endpoints_tried.map((ep, i) => (
                                  <div key={i} className="pl-2 border-l border-[color:var(--border-subtle)]">
                                    <span className="text-[color:var(--text-1)]">{ep.name}</span>
                                    {" · "}
                                    status=<span className={ep.status_code === 200 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>{ep.status_code ?? 0}</span>
                                    {" · "}
                                    count=<span className={ep.count > 0 ? "text-[color:var(--success)]" : "text-[color:var(--text-3)]"}>{ep.count ?? 0}</span>
                                    {ep.error ? (
                                      <div className="text-[color:var(--danger)] break-all pl-2">error: {ep.error}</div>
                                    ) : null}
                                    {ep.body_preview && ep.status_code !== 200 ? (
                                      <div className="text-[color:var(--text-3)] break-all pl-2">body: {ep.body_preview}</div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {dbg.pages_fetch_debug.raw_page_ids?.length ? (
                              <div>discovered_page_ids: <span className="text-[color:var(--text-1)] break-all">{dbg.pages_fetch_debug.raw_page_ids.join(", ")}</span></div>
                            ) : null}
                            {dbg.pages_fetch_debug.raw_page_names?.length ? (
                              <div>discovered_page_names: <span className="text-[color:var(--text-1)] break-all">{dbg.pages_fetch_debug.raw_page_names.join(", ")}</span></div>
                            ) : null}
                            {dbg.pages_fetch_debug.error ? (
                              <div className="text-[color:var(--danger)] break-all">error: {dbg.pages_fetch_debug.error}</div>
                            ) : null}
                          </div>
                        </details>
                      ) : null}
                      {pages.length ? (
                        <div className="mt-1">
                          <div className="text-[color:var(--text-3)] mb-1">pages:</div>
                          <div className="space-y-0.5 pl-2 border-l border-[color:var(--border-subtle)]">
                            {pages.map((p) => (
                              <div key={p.page_id}>
                                <span className="text-[color:var(--text-1)]">{p.page_name}</span>
                                <span className="text-[color:var(--text-3)]"> · {p.page_id}</span>
                                {" · ig="}
                                <span className={p.instagram_business_account_id ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
                                  {p.instagram_business_account_id || "null"}
                                </span>
                                {" · page_token="}
                                <span className={p.has_page_token ? "text-[color:var(--success)]" : "text-[color:var(--text-3)]"}>
                                  {p.has_page_token ? "yes" : "no"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {dbg.last_pages_refresh_at ? (
                        <div className="text-[color:var(--text-3)]">last_refresh: {dbg.last_pages_refresh_at}</div>
                      ) : null}
                      {reqList?.length ? (
                        <div className="mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
                          <div className="text-[color:var(--accent-warm)] mb-1">app review requirements:</div>
                          <ul className="pl-3 space-y-0.5 list-disc text-[color:var(--text-2)]">
                            {reqList.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </details>
            ) : null}
          </div>
        ) : (
          // ---------- OAUTH START (Facebook / X) ----------
          <div className="mt-2">
            <Button
              onClick={() => startOAuth(null)}
              disabled={busy}
              className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid={`customer-oauth-start-${platform}`}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cta className="h-4 w-4 mr-2" />}
              {cfg.primaryCtaLabel}
            </Button>
            <p className="text-[11px] text-[color:var(--text-3)] mt-3 text-center">
              {isFacebook
                ? "You'll be redirected to Facebook to authorize Postmoro. We request the Page and Instagram permissions needed to publish posts on your behalf — review them on the Meta consent screen."
                : `You'll be redirected to ${cfg.label} to authorize Postmoro. We only request the permissions we need to publish posts on your behalf.`}
            </p>
            {isFacebook ? (
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => setFbHelpOpen(true)}
                  className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-3)] hover:text-[color:var(--text-1)] underline underline-offset-2 decoration-dotted transition-colors"
                  data-testid="facebook-connection-help-link"
                >
                  <HelpCircle className="h-3 w-3" />
                  Having trouble connecting Facebook?
                </button>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
      {isFacebook ? (
        <Dialog open={fbHelpOpen} onOpenChange={setFbHelpOpen}>
          <DialogContent
            className="sm:max-w-md bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
            data-testid="facebook-connection-help-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-[color:var(--text-1)]">
                Having trouble connecting Facebook?
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-[color:var(--text-2)] space-y-3">
              <p>
                If Facebook shows an &ldquo;app isn&rsquo;t available&rdquo; or
                connection error, try these steps:
              </p>
              <ol className="list-decimal pl-5 space-y-1.5 text-[color:var(--text-2)]">
                <li>Log out of Facebook completely.</li>
                <li>
                  Log back in using the main personal Facebook account that
                  manages your business Page.
                </li>
                <li>
                  Open your Facebook business Page and confirm that your
                  account still has full/admin access.
                </li>
                <li>
                  Return to Postmoro → Connections → Facebook → Connect.
                </li>
                <li>
                  If Facebook asks you to switch accounts or profiles, choose
                  the personal Facebook account that manages the Page.
                </li>
                <li>
                  If it still fails, try again in a private/incognito browser
                  window.
                </li>
              </ol>
              <p>
                If you still see &ldquo;It looks like this app isn&rsquo;t
                available,&rdquo; take a screenshot of the error including the
                browser address bar and contact Postmoro Support.
              </p>
              <p className="text-[11px] text-[color:var(--text-3)] pt-2 border-t border-[color:var(--border-subtle)]">
                Facebook connection issues can sometimes be caused by the
                Facebook account, Page permissions, or an expired browser
                session.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setFbHelpOpen(false)}
                variant="outline"
                className="border-[color:var(--border-subtle)]"
                data-testid="facebook-connection-help-close"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </Dialog>
  );
}
